import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { CATALOG } from "../lib/site-data";

export default function CatalogPage() {
  const [active, setActive] = useState(CATALOG[0].category);
  const current = CATALOG.find((c) => c.category === active);

  return (
    <main data-testid="services-page">
      <PageHeader
        eyebrow="What we do"
        title="Services."
        accent="Built to last."
        subtitle="Browse our full range of residential and commercial remodeling services. Choose a category to see what we do best."
      />

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          {/* Tabs */}
          <div
            className="flex flex-wrap gap-2 mb-10 border-b border-white/10 pb-1"
            data-testid="catalog-tabs"
          >
            {CATALOG.map((c) => (
              <button
                key={c.category}
                onClick={() => setActive(c.category)}
                className={`relative px-5 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors ${
                  active === c.category
                    ? "text-[#D4AF37]"
                    : "text-slate-400 hover:text-white"
                }`}
                data-testid={`catalog-tab-${c.category.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c.category}
                {active === c.category && (
                  <motion.span
                    layoutId="catalog-underline"
                    className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[#D4AF37]"
                  />
                )}
              </button>
            ))}
          </div>

          {/* Items */}
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
          >
            {current.items.map((item, i) => (
              <motion.article
                key={item.name}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group relative rounded-lg overflow-hidden border border-white/5 bg-[#2a3744]"
                data-testid={`catalog-item-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1f2935] via-[#1f2935]/20 to-transparent opacity-90" />
                </div>
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1.5">
                    {active}
                  </div>
                  <h3 className="font-serif text-lg text-white leading-tight">
                    {item.name}
                  </h3>
                  {item.spec && (
                    <p className="mt-1 text-[12px] text-slate-400">
                      {item.spec}
                    </p>
                  )}
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 border-t border-white/5 bg-[#171f29]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-8 bg-[#D4AF37]" />
                <span className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
                  Have a project?
                </span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-white tracking-tight leading-[1.1]">
                Tell us what you're building — we'll handle the rest.
              </h3>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/quote"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#D4AF37] text-[#1f2935] font-semibold text-[12px] uppercase tracking-[0.22em] hover:bg-[#B48600] transition-colors"
                data-testid="services-cta-quote"
              >
                Get a Quote <ArrowRight size={13} strokeWidth={2} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
